// The chart's recording target: the ONE visit a chart recording goes to
// (owner 10-09: "disable blinkscribe in the sidebar unless the provider
// selects that visit and then tries to record on it. Then we can have them
// record on top of whatever visit they select").
//
//   - A chart opened on the patient's live visit today (visitGuard.js
//     isLiveTodayVisit: today, not cancelled / rescheduled / no-show): that
//     visit is the target, as before.
//   - Any other chart (no visit today, a past / future / cancelled card, a
//     search-opened chart): no target until the provider SELECTS a visit (the
//     CareIQ timeline, or the chart card's own visit offered on the CareIQ "No
//     visit today" screen). The mic is disabled until then.
//   - Once a recording starts, its visit is LOCKED until it stops: a selection
//     made meanwhile is refused (with a notice), never applied.
//
// One module-level store (no React context: the CareIQ tab's tests mock
// RecordingContext, and the store must be the same one for the sidebar, the
// CareIQ tab, RecordingContext and the note editor link). Shared by:
//   sideframe.jsx      - mic enabled / hint / "Selected visit: <date>" label,
//                        the row handed to RecordingContext.startRecording
//   CareIQTabN3.jsx    - the timeline pick (selectRecordingVisit), the
//                        "Viewing visit of ..." banner, "Back to today"
//   RecordingContext   - lockRecordingTarget / unlockRecordingTarget
//   patientchartwrap   - the note editor's chart visit (chartAppointmentId)
//
// A timeline visit (a FHIR encounter row) carries no appointment id: the
// selection resolves it from the dashboard for that day (POST
// /appointments/dashboard, appointmentdate = the visit's day, searchflag =
// the patient: appointment_master_history for a past day). Until then (or
// when no appointment is found) the visit cannot be recorded on.

import { useSyncExternalStore } from "react";
import api from "../api/api";
import { getPracticeId } from "../config/practice";
import {
  classifyVisit,
  hasAppointmentId,
  isCancelledVisit,
  isLiveTodayVisit,
  isResourceSlot,
  longDate,
  rowsOfBody,
  samePatient,
  visitDayKey,
  visitNotHeld,
  visitStartMinutes,
  visitTimeLabel,
} from "./visitGuard.js";

// ---------------------------------------------------------------- pure parts

// One chart card = one key (the CareIQ tab's own cardKey): a selection made on
// one card never applies to another card of the same patient.
export const chartKeyOf = (card) =>
  `${card?.patientid ?? ""}:${card?.appointmentid ?? ""}:${card?.startdate ?? ""}`;

// "2026-09-11" -> "Sep 11, 2026" (visitGuard.js longDate).
export { longDate };

// The dashboard row of the picked visit's day: the encounter's own row when
// the encounter id matches, else that day's provider visit (resource slots
// last) that happened, earliest first; else any row of the day (a cancelled /
// no-show one: the consent then warns). null when the day has none.
export const pickVisitDayRow = (rows, patientid, dayKey, hint = {}) => {
  const list = (Array.isArray(rows) ? rows : []).filter(
    (r) =>
      r &&
      samePatient(r.patientid, patientid) &&
      hasAppointmentId(r.appointmentid) &&
      visitDayKey(r) === dayKey,
  );
  if (!list.length) return null;
  const enc = String(hint?.encounterid ?? "").trim();
  if (enc) {
    const byEnc = list.find((r) => String(r.encounterid ?? "").trim() === enc);
    if (byEnc) return byEnc;
  }
  const key = (r) => {
    const m = visitStartMinutes(r);
    return m === null ? 24 * 60 : m;
  };
  return [...list].sort(
    (a, b) =>
      (visitNotHeld(a) ? 1 : 0) - (visitNotHeld(b) ? 1 : 0) ||
      (isCancelledVisit(a) ? 1 : 0) - (isCancelledVisit(b) ? 1 : 0) ||
      (isResourceSlot(a) ? 1 : 0) - (isResourceSlot(b) ? 1 : 0) ||
      key(a) - key(b) ||
      Number(a.appointmentid) - Number(b.appointmentid),
  )[0];
};

// Per-appointment fields of a chart card that belonged to ANOTHER appointment.
const RESET_FIELDS = {
  encounterid: null,
  lov: null,
  tov: null,
  lovtov: null,
  room_no: "",
  orders: [],
  allappts: [],
};
const APPT_FIELDS = [
  "id",
  "appointmentid",
  "departmentid",
  "providerid",
  "startdate",
  "starttime",
  "start_time",
  "appointmenttypeid",
  "visit_confirmation",
  "appointmentstatus",
  "status",
  "encounterid",
  "purpose",
  "linked_provider",
  "providername",
];

// The chart card (patient fields: name, DOB... which the recording's file
// name and BlinkScribe's patientname need) carrying another appointment.
export const cardForVisit = (card, appt) => {
  const out = { ...(card || {}), ...RESET_FIELDS };
  delete out.history;
  // every appointment field from `appt` (none of the card's other appointment survives)
  for (const f of APPT_FIELDS) {
    out[f] = appt && appt[f] !== undefined ? appt[f] : null;
  }
  out.start_time = visitTimeLabel(appt) || null;
  out.patientid = card?.patientid ?? appt?.patientid;
  return out;
};

// What a chart can record on now. state: the store's state (below).
//   status: "locked"     - a recording of this patient runs; row = its visit
//           "today"      - the chart's live visit today
//           "selected"   - a selected visit with its appointment (recordable)
//           "resolving"  - a selected visit whose appointment is being found
//           "unresolved" - a selected visit without an appointment (no mic)
//           "none"       - nothing selected (no mic)
//   row: the card-shaped row to record on (null unless locked/today/selected)
//   info: classifyVisit(row) (or of the pick while it has no row)
//   dayKey: the target / selected visit's day ("" for none)
//   canRecord: the mic may start a recording on `row`
//   selected: a visit the provider selected is the target (not the chart card)
//   hint: why the mic is off ("" when it is on)
export const recordingTargetFor = (card, state, now = new Date()) => {
  const st = state || {};
  const lock = st.lock;
  if (lock && samePatient(lock.patientid, card?.patientid)) {
    const info = lock.info || classifyVisit(lock.row, now);
    return {
      status: "locked",
      row: lock.row,
      info,
      dayKey: info?.dayKey || "",
      canRecord: false,
      selected: Boolean(lock.selected),
      hint: "",
    };
  }
  if (isLiveTodayVisit(card, now)) {
    const info = classifyVisit(card, now);
    return { status: "today", row: card, info, dayKey: info.dayKey || "", canRecord: true, selected: false, hint: "" };
  }
  const sel = st.selection;
  if (sel && sel.chartKey === chartKeyOf(card) && samePatient(sel.patientid, card?.patientid)) {
    const dayKey = visitDayKey(sel.row || sel.pick) || "";
    const date = longDate(dayKey);
    if (sel.row) {
      return {
        status: "selected",
        row: sel.row,
        info: classifyVisit(sel.row, now),
        dayKey,
        canRecord: true,
        selected: true,
        hint: "",
      };
    }
    if (sel.status === "resolving") {
      return {
        status: "resolving",
        row: null,
        info: classifyVisit(sel.pick, now),
        dayKey,
        canRecord: false,
        selected: true,
        hint: date ? `Finding the ${date} visit…` : "Finding the visit…",
      };
    }
    return {
      status: "unresolved",
      row: null,
      info: classifyVisit(sel.pick, now),
      dayKey,
      canRecord: false,
      selected: true,
      hint: date
        ? `No appointment found for the ${date} visit, so it cannot be recorded on.`
        : "No appointment found for this visit, so it cannot be recorded on.",
    };
  }
  return {
    status: "none",
    row: null,
    info: classifyVisit(card, now),
    dayKey: "",
    canRecord: false,
    selected: false,
    hint: "Select a visit on the timeline to record",
  };
};

// "Recording in progress on the Sep 11, 2026 visit. Stop it to choose another visit."
export const lockedNotice = (lock) => {
  const day = lock?.info?.dayKey || visitDayKey(lock?.row) || "";
  const which = lock?.info?.kind === "today" && !lock?.selected ? "today's visit" : day ? `the ${longDate(day)} visit` : "its visit";
  return `Recording in progress on ${which}. Stop it to choose another visit.`;
};

// --------------------------------------------------------------------- store

let state = { selection: null, lock: null, notice: "" };
const listeners = new Set();
let seq = 0;

const publish = (next) => {
  state = next;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {
      /* a listener never breaks the others */
    }
  });
};

export const getRecordingTargetState = () => state;

export const subscribeRecordingTarget = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useRecordingTargetState = () =>
  useSyncExternalStore(subscribeRecordingTarget, getRecordingTargetState, getRecordingTargetState);

// The dashboard search for one day (the chart resolver's own call, any date).
// Injected for tests: configureRecordingTarget({ searchDay }).
let searchDayImpl = null;
export const configureRecordingTarget = ({ searchDay } = {}) => {
  searchDayImpl = typeof searchDay === "function" ? searchDay : null;
};
const defaultSearchDay = (patientid, dayKey) =>
  api.post(`/v1/${getPracticeId()}/appointments/dashboard`, {
    appointmentdate: dayKey,
    searchflag: String(patientid),
    dashfilter: null,
    filterarr: [],
    start: 0,
    limit: 0,
    // an explicit patient search: the visit counts at either location
    alldepts: true,
  });

// Select `pick` (a card-shaped visit: the chart card itself, or a timeline
// visit from N3/noVisit.js visitFromTimeline) as the recording target of the
// chart `card`. Refused while a recording runs ({ ok: false, reason:
// "locked" }; the store's notice says why). A pick without an appointment id
// is resolved from that day's dashboard rows.
export const selectRecordingVisit = (card, pick) => {
  if (state.lock) {
    publish({ ...state, notice: lockedNotice(state.lock) });
    return { ok: false, reason: "locked" };
  }
  if (!pick) {
    clearRecordingVisit();
    return { ok: true };
  }
  const chartKey = chartKeyOf(card);
  const patientid = card?.patientid ?? pick.patientid;
  const mySeq = ++seq;
  if (hasAppointmentId(pick.appointmentid)) {
    const row = sameCardVisit(card, pick) ? { ...card } : cardForVisit(card, pick);
    publish({ ...state, notice: "", selection: { chartKey, patientid, pick, row, status: "ready", seq: mySeq } });
    return { ok: true };
  }
  const dayKey = visitDayKey(pick);
  publish({ ...state, notice: "", selection: { chartKey, patientid, pick, row: null, status: dayKey ? "resolving" : "unresolved", seq: mySeq } });
  if (!dayKey || patientid === undefined || patientid === null || patientid === "") return { ok: true };
  const search = searchDayImpl || defaultSearchDay;
  Promise.resolve()
    .then(() => search(patientid, dayKey))
    .then((body) => pickVisitDayRow(rowsOfBody(body), patientid, dayKey, { encounterid: pick.encounterid }))
    .catch(() => null)
    .then((appt) => {
      const sel = state.selection;
      if (!sel || sel.seq !== mySeq) return; // superseded / cleared
      publish({
        ...state,
        selection: {
          ...sel,
          row: appt ? cardForVisit(card, appt) : null,
          status: appt ? "ready" : "unresolved",
        },
      });
    });
  return { ok: true };
};

const sameCardVisit = (card, pick) =>
  hasAppointmentId(card?.appointmentid) && Number(card.appointmentid) === Number(pick?.appointmentid);

// Back to today / chart closed / another chart: no selected visit. Never
// touches a lock (the recording's visit stays until it stops).
export const clearRecordingVisit = () => {
  seq += 1;
  if (!state.selection && !state.notice) return;
  publish({ ...state, selection: null, notice: "" });
};

export const dismissRecordingNotice = () => {
  if (state.notice) publish({ ...state, notice: "" });
};

// RecordingContext: a recording started on `row` (its classifyVisit `info`);
// `selected` = the row came from a selection, not the chart's live visit.
export const lockRecordingTarget = (row, info, { selected = false } = {}) => {
  if (!row) return;
  publish({ ...state, lock: { patientid: row.patientid, row, info: info || classifyVisit(row), selected } });
};

export const unlockRecordingTarget = () => {
  if (!state.lock && !state.notice) return;
  publish({ ...state, lock: null, notice: "" });
};

// tests only
export const __resetRecordingTarget = () => {
  seq += 1;
  searchDayImpl = null;
  state = { selection: null, lock: null, notice: "" };
  listeners.forEach((fn) => fn());
};
